// Error screens. Every route has one (routes.tsx `errorElement`): a crash in a section renders
// inside the shell, so the rail and the tab bar keep working; the root route has the `top`
// variant for a crash in the shell itself. A section chunk that failed to load (offline before the
// service worker had it, or an update replaced it) reloads the page once by itself.
// Text in the UI language (i18n/areas/shell.ts); the details are collapsed for a flag.
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router';
import { useT } from '../i18n';
import { clearRemoteCache } from '../lib/remote';
import { componentStackOf } from './crashLog';

const RELOADED = 'valyrio:chunk-reload';

/** "Failed to fetch dynamically imported module" and the other browsers' wordings. */
export function isChunkError(err: unknown): boolean {
  const m = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|ChunkLoadError|Unable to preload CSS/i.test(m);
}

function describe(err: unknown): string {
  if (isRouteErrorResponse(err)) return `${err.status} ${err.statusText}`;
  if (err instanceof Error) return `${err.name}: ${err.message}\n${err.stack ?? ''}`.trim();
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/** Reload once per path and minute; true when the page is reloading (no sessionStorage: false). */
function reloadOnce(pathname: string): boolean {
  try {
    const last = JSON.parse(sessionStorage.getItem(RELOADED) ?? 'null') as { path: string; at: number } | null;
    if (last && last.path === pathname && Date.now() - last.at < 60_000) return false;
    sessionStorage.setItem(RELOADED, JSON.stringify({ path: pathname, at: Date.now() }));
    location.reload();
    return true;
  } catch {
    return false; // no sessionStorage: show the screen
  }
}

export function RouteError({ top = false }: { top?: boolean }) {
  const t = useT();
  const E = t.shell.error;
  const err = useRouteError();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const chunk = isChunkError(err);
  const [clearing, setClearing] = useState(false);

  // A chunk that failed to load: reload once (per path, per minute) before showing anything.
  // Decided in an effect, not during render: React may render an error screen more than once
  // before committing it, and only the committed one may spend the single reload.
  const [reloading, setReloading] = useState(chunk);
  useEffect(() => {
    if (!chunk || reloadOnce(pathname)) return;
    // oxlint-disable-next-line react/set-state-in-effect -- decided after commit on purpose (above)
    setReloading(false);
  }, [chunk, pathname]);
  // React Router's onError (main.tsx) logs the error with its component stack while this screen
  // commits; read the stack after that, for the details below.
  const [stack, setStack] = useState('');
  const [at] = useState(() => new Date().toISOString());
  useEffect(() => {
    console.error('[route error]', pathname, err);
    // oxlint-disable-next-line react/set-state-in-effect -- the stack exists only after this screen commits
    setStack(componentStackOf(err).trim());
  }, [err, pathname]);

  if (reloading) return null;

  // A lazy chunk that failed stays failed in memory: only a reload loads it again.
  const retry = () => (chunk || top ? location.reload() : navigate(pathname + search, { replace: true }));
  const home = () => (top ? location.assign('/') : navigate('/'));
  const clear = async () => {
    setClearing(true);
    await clearRemoteCache();
    location.reload();
  };
  const details = [describe(err), ...(stack ? [`component stack:\n${stack}`] : []), `${pathname}${search}`, at, navigator.userAgent].join('\n');

  return (
    <div className={top ? 'page page-text route-error is-top' : 'page page-text route-error'} role="alert">
      <h1 className="page-h" tabIndex={-1}>
        {chunk ? E.chunkTitle : top ? E.appTitle : E.title}
      </h1>
      <p className="set-p">{chunk ? E.chunkLead : top ? E.appLead : E.lead}</p>
      <div className="row-actions">
        <button type="button" className="btn btn-primary" onClick={retry}>
          {E.retry}
        </button>
        {pathname !== '/' ? (
          <button type="button" className="btn" onClick={home}>
            {E.home}
          </button>
        ) : null}
      </div>
      {!chunk ? (
        <div className="route-error-cache">
          <p className="set-note">{E.clearCacheNote}</p>
          <button type="button" className="btn btn-small" onClick={() => void clear()} disabled={clearing}>
            {clearing ? E.clearing : E.clearCache}
          </button>
        </div>
      ) : null}
      <details className="route-error-details">
        <summary>{E.details}</summary>
        <p className="set-note">{E.detailsNote}</p>
        <pre>{details}</pre>
      </details>
    </div>
  );
}

/** The root route's error screen: the shell itself crashed, so this stands alone. */
export function AppError() {
  return (
    <div className="shell-error">
      <main className="main" id="main">
        <RouteError top />
      </main>
    </div>
  );
}
